import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  FaArrowLeft,
  FaMicrophone,
  FaStop,
  FaStopwatch,
  FaVolumeUp,
  FaSpinner,
  FaChevronDown,
  FaChevronUp,
  FaEdit,
  FaPlay,
  FaCheck,
} from 'react-icons/fa';
import { MdSkipNext, MdSend, MdWarning } from 'react-icons/md';
import { useNavigate, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import { useWindowSize } from 'react-use';
import { captureException } from '@sentry/react';
import { useHotkeys } from 'react-hotkeys-hook';
import api from '../../../libs/axiosInstance';
import firebase from 'firebase/app';
import 'firebase/auth';
import 'firebase/firebase-firestore';
import 'firebase/storage';
import { Collections } from '../../../types/collection';
import Countdown from '../components/Countdown';
import { INTERVIEW_TYPES } from '../../../pages/NewSession';
import { useUserContext } from '../../../contexts/user.context';
import Confetti from 'react-confetti';
import { sessionCompletionService } from '../../../services/session-completion.service';
import { FormInputs } from '../../../types/session-forms.types';
import { Question } from '../../../interfaces/question.interface';
import { RunData } from '../../../interfaces/run.interface';
import { Subscriptions } from '../../payments/enhanced-pricing-plans.type';
import { apiUsageService } from '../../../services/api-usage.service';

// Constants for recording
const MAX_RECORDING_TIME_MS = 5 * 60 * 1000; // 5 minutes

export interface ExpressionData {
  angry: number;
  disgusted: number;
  fearful: number;
  happy: number;
  neutral: number;
  sad: number;
  surprised: number;
}

/* eslint-disable @typescript-eslint/no-explicit-any */
// For speech-to-text
const SpeechRecognitionConstructor =
  (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
/* eslint-enable @typescript-eslint/no-explicit-any */

// Define speech synthesis API
const speechSynthesis = window.speechSynthesis;

// ElevenLabs voice IDs - these are some of their premium voices
const ELEVENLABS_VOICES = {
  rachel: "21m00Tcm4TlvDq8ikWAM", // Rachel - warm, natural female
  adam: "pNInz6obpgDQGcFmaJgB",   // Adam - professional male
  antoni: "ErXwobaYiN019PkySvjV", // Antoni - warm male
  bella: "EXAVITQu4vr4xnSDxMaL",  // Bella - natural female
  sam: "yoZ06aMxZJJ28mfd3POQ",    // Sam - casual conversational
};

const AudioActiveSession: React.FC = () => {
  const {
    // reloadUserData, 
    user } = useUserContext();
  const toast = useRef<Toast>(null);
  const navigate = useNavigate();
  const { sessionId, runId } = useParams();

  const [sessionData, setSessionData] = useState<FormInputs>();
  const [sessionQuestions, setSessionQuestions] = useState<Question[]>();
  const [currentQuestion, setCurrentQuestion] = useState(0);

  // Transcription
  const [transcript, setTranscript] = useState('');
  const recognitionRef = useRef<SpeechRecognition | null>(null);
  const [isListening, setIsListening] = useState(false);

  // Recording states
  const [isRecording, setIsRecording] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder | null>(null);
  const recordedBlobsRef = useRef<Blob[]>([]);

  // Microphone access
  const streamRef = useRef<MediaStream | null>(null);

  // Speaking detection
  const [isSpeaking, setIsSpeaking] = useState(false);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const dataArrayRef = useRef<Uint8Array | null>(null);
  const animationFrameRef = useRef<number | null>(null);

  // Text-to-speech states
  const [isReading, setIsReading] = useState(false);
  const [isLoadingVoice, setIsLoadingVoice] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const speechSynthesisRef = useRef<SpeechSynthesisUtterance | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const intentionalCancelRef = useRef(false);
  const hasAutoReadRef = useRef<number>(-1);
  const [interviewerSpeaking, setInterviewerSpeaking] = useState(false);

  // Submission state
  const [submitting, toggleSubmitting] = useState(false);

  // Overall session countdown
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [showRunCompleteDialog, toggleShowRunCompleteDialog] = useState(false);

  // Recording timer
  const [recordingTimeLeft, setRecordingTimeLeft] = useState(
    MAX_RECORDING_TIME_MS / 1000
  );
  const recordingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const recordingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // For Confetti sizing
  const { width, height } = useWindowSize();

  // New state variables for improvements
  const [isLoading, setIsLoading] = useState(true);
  const [hasPermission, setHasPermission] = useState<boolean | null>(null);
  const [isRequestingPermission, setIsRequestingPermission] = useState(false);
  const [showTips, setShowTips] = useState(true);
  const [isEditingTranscript, setIsEditingTranscript] = useState(false);
  const [editedTranscript, setEditedTranscript] = useState('');
  const [hasRecording, setHasRecording] = useState(false);
  const [recordingUrl, setRecordingUrl] = useState<string | null>(null);
  const [isPlayingRecording, setIsPlayingRecording] = useState(false);
  const [audioLevel, setAudioLevel] = useState(0);
  const [autoReadEnabled, setAutoReadEnabled] = useState(true);
  const playbackAudioRef = useRef<HTMLAudioElement | null>(null);

  // Add sound wave animations
  useEffect(() => {
    // Create a style element
    const styleEl = document.createElement('style');
    styleEl.textContent = `
      @keyframes sound-wave-1 {
        0% { height: 3px; }
        50% { height: 10px; }
        100% { height: 3px; }
      }
      @keyframes sound-wave-2 {
        0% { height: 5px; }
        50% { height: 15px; }
        100% { height: 5px; }
      }
      @keyframes sound-wave-3 {
        0% { height: 7px; }
        50% { height: 20px; }
        100% { height: 7px; }
      }
      .animate-sound-wave-1 {
        animation: sound-wave-1 1s ease-in-out infinite;
      }
      .animate-sound-wave-2 {
        animation: sound-wave-2 0.8s ease-in-out infinite;
      }
      .animate-sound-wave-3 {
        animation: sound-wave-3 1.2s ease-in-out infinite;
      }
    `;
    document.head.appendChild(styleEl);

    // Clean up on unmount
    return () => {
      document.head.removeChild(styleEl);
    };
  }, []);

  // Helper: format time as mm:ss
  const formatTime = (totalSeconds: number) => {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds < 10 ? `0${seconds}` : seconds}`;
  };

  /* -----------------------------------------------------
   * Call ElevenLabs API for human-like TTS using interviewer voice
   * ----------------------------------------------------- */
  const generateSpeechWithElevenLabs = async (text: string): Promise<string> => {
    // ElevenLabs API direct implementation
    try {
      // Check if we have an interviewer voice ID from the session data
      // This will use the interviewer selected during session creation
      const response = await fetch('https://api.elevenlabs.io/v1/text-to-speech/' +
        (sessionData?.interviewer?.voiceId || ELEVENLABS_VOICES.rachel), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'xi-api-key': import.meta.env.VITE_ELEVEN_LABS,
        },
        body: JSON.stringify({
          text: text,
          model_id: 'eleven_multilingual_v2',
          voice_settings: {
            stability: 0.5,
            similarity_boost: 0.75,
            style: 0.0,
            use_speaker_boost: true
          }
        })
      });

      if (!response.ok) {
        throw new Error(`ElevenLabs API error: ${response.status}`);
      }

      // Get the audio data as a blob
      const audioBlob = await response.blob();

      // Create a URL for the audio blob
      return URL.createObjectURL(audioBlob);
    } catch (error) {
      console.error('ElevenLabs API error:', error);
      throw new Error('Failed to generate speech with ElevenLabs');
    }
  };

  /* -----------------------------------------------------
   * Text-to-Speech for Questions
   * ----------------------------------------------------- */
  const readQuestionAloud = useCallback(async () => {
    // Check if user's plan allows voice functionality
    if (user?.subscription.name !== Subscriptions.pro && user?.subscription.name !== Subscriptions.enterprise) {
      toast.current?.show({
        severity: 'info',
        summary: 'Premium Feature',
        detail: 'Voice playback is available on Pro and Enterprise plans.',
        life: 3000,
      });
      return;
    }

    if (!sessionQuestions) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'No question available to read.',
      });
      return;
    }

    // If already playing audio, stop it
    if (isReading) {
      setIsReading(false);
      setApiError(null);
      setInterviewerSpeaking(false);
      intentionalCancelRef.current = true;

      // Stop browser speech synthesis if it's being used
      if (speechSynthesis) {
        speechSynthesis.cancel();
      }

      // Stop API-based audio if it's playing
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }

      return;
    }

    try {
      const questionText = sessionQuestions[currentQuestion]?.question;
      if (!questionText) return;

      // Try ElevenLabs API first
      try {
        setIsLoadingVoice(true);
        setApiError(null);
        setInterviewerSpeaking(true);

        // Get audio URL from API
        const audioUrl = await generateSpeechWithElevenLabs(questionText);

        // Create audio element if it doesn't exist
        if (!audioRef.current) {
          audioRef.current = new Audio();

          // Add event listeners
          audioRef.current.addEventListener('ended', () => {
            setIsReading(false);
            setInterviewerSpeaking(false);
          });

          audioRef.current.addEventListener('error', (e) => {
            console.error('Audio playback error:', e);
            setIsReading(false);
            setInterviewerSpeaking(false);
            setApiError('Error playing audio');
            toast.current?.show({
              severity: 'error',
              summary: 'Playback Error',
              detail: 'Could not play the generated speech.',
            });
          });
        }

        // Set source and play
        audioRef.current.src = audioUrl;
        await audioRef.current.play();
        setIsReading(true);
        setIsLoadingVoice(false);

      } catch (error) {
        console.error('ElevenLabs API error, falling back to browser TTS:', error);
        setApiError('Failed to generate premium voice');
        setIsLoadingVoice(false);
        setInterviewerSpeaking(false);

        // Fall back to browser speech synthesis
        if (speechSynthesis) {
          fallbackToBrowserTTS(questionText);
        } else {
          toast.current?.show({
            severity: 'error',
            summary: 'Speech Error',
            detail: 'Could not generate speech. Please try again.',
          });
        }
      }
    } catch (error) {
      console.error('Text-to-speech error:', error);
      setIsReading(false);
      setIsLoadingVoice(false);
      setInterviewerSpeaking(false);
      setApiError(null);
      toast.current?.show({
        severity: 'error',
        summary: 'Speech Error',
        detail: 'An error occurred while trying to read the question.',
      });
    }
  }, [sessionQuestions, currentQuestion, isReading, sessionData?.interviewer?.voiceId, user?.subscription.name]);

  // Update the fallback function to also set interviewer speaking state
  const fallbackToBrowserTTS = useCallback((text: string) => {
    if (!speechSynthesis) return;

    try {
      const utterance = new SpeechSynthesisUtterance(text);

      // Get available voices
      const voices = speechSynthesis.getVoices();

      // If voices array is empty, we need to wait for voices to load
      if (voices.length === 0) {
        toast.current?.show({
          severity: 'info',
          summary: 'Loading Voices',
          detail: 'Please try again in a moment.',
        });
        return;
      }

      // Try to find the best voice available using our previous algorithm
      const premiumVoiceKeywords = [
        'premium', 'enhanced', 'neural', 'wavenet', 'Daniel', 'Samantha', 'Alex',
        'Karen', 'Moira', 'Tessa', 'Allison', 'Ava', 'Susan', 'Joanna'
      ];

      let selectedVoice = voices.find(voice =>
        voice.lang.includes(navigator.language.split('-')[0]) &&
        premiumVoiceKeywords.some(keyword => voice.name.includes(keyword))
      );

      if (!selectedVoice) {
        selectedVoice = voices.find(voice =>
          voice.lang.includes('en') &&
          premiumVoiceKeywords.some(keyword => voice.name.includes(keyword))
        );
      }

      if (!selectedVoice && voices.length > 0) {
        selectedVoice = voices[0];
      }

      if (selectedVoice) {
        utterance.voice = selectedVoice;
      }

      // Adjust for most human-like speech
      utterance.rate = 0.85;
      utterance.pitch = 1.05;
      utterance.volume = 1.0;

      // Reset intentional cancel flag
      intentionalCancelRef.current = false;

      // Event handlers
      utterance.onstart = () => {
        setIsReading(true);
        setInterviewerSpeaking(true);
      };
      utterance.onend = () => {
        setIsReading(false);
        setInterviewerSpeaking(false);
      };
      utterance.onerror = (event) => {
        console.error('Speech synthesis error:', event);

        if (!intentionalCancelRef.current) {
          setIsReading(false);
          setInterviewerSpeaking(false);
          toast.current?.show({
            severity: 'error',
            summary: 'Read Error',
            detail: 'Could not read question aloud. Please try again.',
          });
        }

        intentionalCancelRef.current = false;
      };

      // Store reference for cleanup
      speechSynthesisRef.current = utterance;

      // Cancel any previous speech
      speechSynthesis.cancel();

      // Speak
      speechSynthesis.speak(utterance);
    } catch (error) {
      console.error('Browser TTS fallback error:', error);
      setIsReading(false);
      setInterviewerSpeaking(false);
      toast.current?.show({
        severity: 'error',
        summary: 'Speech Error',
        detail: 'Could not read question aloud.',
      });
    }
  }, []);

  /* --------------------------------
   * Fetch and Listen for changes
   * ------------------------------- */

  // Reload user data
  /* useEffect(() => {
    reloadUserData();
  }, [reloadUserData]); */

  // Fetch session data
  useEffect(() => {
    async function getSessionData() {
      try {
        const userId = firebase.auth().currentUser?.uid;
        if (!userId) return;
        firebase
          .firestore()
          .collection(Collections.Sessions)
          .doc(userId)
          .collection('list')
          .doc(sessionId!)
          .onSnapshot((snapshot) => {
            if (!snapshot.exists) {
              setIsLoading(false);
              return;
            }
            const data = snapshot.data() as FormInputs;
            setSessionData({ ...data, id: snapshot.id });
            setIsLoading(false);
          });
      } catch (error) {
        captureException(error);
        setIsLoading(false);
        toast.current?.show({
          severity: 'error',
          summary: '📊 Loading Error',
          detail: 'Could not load your session data. Please refresh the page and try again.',
          life: 4000,
        });
      }
    }
    getSessionData();
  }, [sessionId]);

  // Fetch run data & questions
  useEffect(() => {
    async function getSessionQuestions() {
      try {
        const userId = firebase.auth().currentUser?.uid;
        if (!userId) {
          setIsLoading(false);
          return;
        }
        firebase
          .firestore()
          .collection(Collections.Runs)
          .doc(sessionId)
          .collection('list')
          .doc(runId)
          .onSnapshot((snapshot) => {
            if (!snapshot.exists) {
              setIsLoading(false);
              return;
            }
            const runData = snapshot.data() as RunData;
            if (runData.completed) {
              toggleShowRunCompleteDialog(true);
            }
            const sortedQuestions = runData.questions?.sort(
              (a, b) => a.id - b.id
            );
            setSessionQuestions(sortedQuestions);
            setCurrentQuestion(runData.currentQuestion || 0);
            setTimeRemaining(runData.timeRemaining || 0);
            setIsLoading(false);
          });
      } catch (error) {
        captureException(error);
        setIsLoading(false);
        toast.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load session questions.',
        });
      }
    }
    getSessionQuestions();
  }, [sessionId, runId]);

  /* --------------------------------
   * Initialize Speech Recognition
   * ------------------------------- */
  useEffect(() => {
    if (!SpeechRecognitionConstructor) {
      captureException('Speech recognition is not supported in your browser.');
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Speech recognition is not supported in your browser.',
      });
      return;
    }

    const recognition = new SpeechRecognitionConstructor();
    recognition.lang = window.navigator.language || 'en-US';
    recognition.continuous = true;
    recognition.interimResults = true;

    recognition.onresult = (event: SpeechRecognitionEvent) => {
      let interimTranscript = '';
      let finalTranscript = '';

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcriptChunk = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalTranscript += transcriptChunk;
        } else {
          interimTranscript += transcriptChunk;
        }
      }
      setTranscript(finalTranscript + interimTranscript);
    };

    recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
      captureException(event.error);
    };

    recognitionRef.current = recognition;
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // If still recording, stop
      if (isRecording) stopRecording();

      // Stop all media tracks (microphone)
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }

      // If still listening, stop
      if (recognitionRef.current && isListening) {
        try {
          recognitionRef.current.stop();
        } catch (e) {
          // Ignore errors
        }
      }

      // Stop audio metering
      stopAudioMeter();

      // Clear audio context
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* --------------------------------
   * Audio Meter (speaking detection)
   * ------------------------------- */
  const updateAudioMeter = () => {
    if (!analyserRef.current || !dataArrayRef.current) return;

    // TypeScript workaround for ArrayBufferLike vs ArrayBuffer type mismatch
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    analyserRef.current.getByteTimeDomainData(dataArrayRef.current as any);

    let sum = 0;
    for (let i = 0; i < dataArrayRef.current.length; i++) {
      const value = (dataArrayRef.current[i] - 128) / 128;
      sum += value * value;
    }
    const rms = Math.sqrt(sum / dataArrayRef.current.length);

    // Adjust threshold as needed
    const threshold = 0.01;
    setIsSpeaking(rms > threshold);

    // Track audio level for quality indicator (0-100)
    const normalizedLevel = Math.min(rms * 1000, 100);
    setAudioLevel(normalizedLevel);

    animationFrameRef.current = requestAnimationFrame(updateAudioMeter);
  };

  const startAudioMeter = (stream: MediaStream) => {
    if (!audioContextRef.current) {
      audioContextRef.current = new AudioContext();
    }
    analyserRef.current = audioContextRef.current.createAnalyser();
    analyserRef.current.fftSize = 2048;
    dataArrayRef.current = new Uint8Array(analyserRef.current.frequencyBinCount) as Uint8Array;

    sourceRef.current = audioContextRef.current.createMediaStreamSource(stream);
    sourceRef.current.connect(analyserRef.current);

    animationFrameRef.current = requestAnimationFrame(updateAudioMeter);
  };

  const stopAudioMeter = () => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    if (sourceRef.current) {
      sourceRef.current.disconnect();
      sourceRef.current = null;
    }
    if (analyserRef.current) {
      analyserRef.current.disconnect();
      analyserRef.current = null;
    }
    // Not closing the audioContext to allow reuse
  };

  /* --------------------------------
   * Permission Handling
   * ------------------------------- */
  const checkMicrophonePermission = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      setHasPermission(true);
      // Release stream immediately after checking
      stream.getTracks().forEach(track => track.stop());
    } catch (error) {
      setHasPermission(false);
      captureException(error);
    }
  }, []);

  // Check microphone permission on mount
  useEffect(() => {
    checkMicrophonePermission();
  }, [checkMicrophonePermission]);

  const requestMicrophonePermission = async () => {
    setIsRequestingPermission(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      setHasPermission(true);
      stream.getTracks().forEach(track => track.stop());
      toast.current?.show({
        severity: 'success',
        summary: 'Permission Granted',
        detail: 'Microphone access has been granted.',
        life: 2000,
      });
    } catch (error: any) {
      setHasPermission(false);
      captureException(error);
      toast.current?.show({
        severity: 'error',
        summary: 'Permission Denied',
        detail: 'Please allow microphone access to record your answers. You can grant permission in your browser settings.',
        life: 5000,
      });
    } finally {
      setIsRequestingPermission(false);
    }
  };

  /* --------------------------------
   * Recording: Start / Stop
   * ------------------------------- */
  const startRecording = async () => {
    // Check permission first
    if (hasPermission === false) {
      await requestMicrophonePermission();
      return;
    }

    if (hasPermission === null) {
      setIsRequestingPermission(true);
      try {
        await requestMicrophonePermission();
        if (!hasPermission) return;
      } catch (error) {
        setIsRequestingPermission(false);
        return;
      }
    }

    try {
      setTranscript('');
      setEditedTranscript('');
      setIsEditingTranscript(false);
      recordedBlobsRef.current = [];
      setHasRecording(false);
      setRecordingUrl(null);

      // Start speech recognition
      if (recognitionRef.current && !isListening) {
        recognitionRef.current.start();
        setIsListening(true);
      }

      // Request audio from user
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      startAudioMeter(stream);

      let options: MediaRecorderOptions = { mimeType: 'audio/webm' };
      if (!MediaRecorder.isTypeSupported(options.mimeType!)) {
        options = { mimeType: 'audio/webm; codecs=opus' };
      }

      const recorder = new MediaRecorder(stream, options);
      setMediaRecorder(recorder);

      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          recordedBlobsRef.current.push(event.data);
        }
      };
      recorder.onstop = () => {
        setMediaRecorder(null);
        // Create blob URL for playback
        const finalBlob = new Blob(recordedBlobsRef.current, {
          type: recorder.mimeType || 'audio/webm',
        });
        if (finalBlob.size > 0) {
          const url = URL.createObjectURL(finalBlob);
          setRecordingUrl(url);
          setHasRecording(true);
        }
      };

      recorder.start(100);
      setIsRecording(true);

      // Clear existing intervals/timeouts
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
        recordingIntervalRef.current = null;
      }
      if (recordingTimeoutRef.current) {
        clearTimeout(recordingTimeoutRef.current);
        recordingTimeoutRef.current = null;
      }

      // Reset countdown
      setRecordingTimeLeft(MAX_RECORDING_TIME_MS / 1000);

      // Interval to decrement UI display
      recordingIntervalRef.current = setInterval(() => {
        setRecordingTimeLeft((prev) => {
          if (prev <= 1) {
            stopRecording();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      // Timeout to force stop
      recordingTimeoutRef.current = setTimeout(() => {
        stopRecording();
        submitAnswer();
      }, MAX_RECORDING_TIME_MS);
    } catch (err) {
      captureException(err);
      toast.current?.show({
        severity: 'error',
        summary: 'Recording Error',
        detail: 'Failed to start audio recording. Please try again.',
      });
    }
  };

  const stopRecording = () => {
    if (recordingIntervalRef.current) {
      clearInterval(recordingIntervalRef.current);
      recordingIntervalRef.current = null;
    }
    if (recordingTimeoutRef.current) {
      clearTimeout(recordingTimeoutRef.current);
      recordingTimeoutRef.current = null;
    }
    setRecordingTimeLeft(MAX_RECORDING_TIME_MS / 1000);

    // Stop speech recognition
    if (recognitionRef.current && isListening) {
      recognitionRef.current.stop();
      setIsListening(false);
    }

    // Stop audio meter
    stopAudioMeter();

    // Stop the MediaRecorder
    if (mediaRecorder) {
      mediaRecorder.requestData();
      mediaRecorder.stop();
    }

    // Release the mic
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }

    setIsRecording(false);
    setAudioLevel(0);
  };

  /* --------------------------------
   * Submit Answer
   * ------------------------------- */
  const submitAnswer = async () => {
    try {
      if (!sessionData || !sessionQuestions) return;
      toggleSubmitting(true);

      toast.current?.show({
        severity: 'info',
        summary: 'Please wait...',
        detail: 'Saving your feedback...',
      });

      const userId = firebase.auth().currentUser?.uid;
      if (!userId) {
        toggleSubmitting(false);
        toast.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: 'User not authenticated.',
        });
        return;
      }

      // Construct final Blob
      const finalBlob = new Blob(recordedBlobsRef.current, {
        type: mediaRecorder?.mimeType || 'audio/webm',
      });

      if (finalBlob.size === 0) {
        toggleSubmitting(false);
        toast.current?.show({
          severity: 'warn',
          summary: '🎤 No Recording Found',
          detail: 'Please record your answer before submitting. Click the microphone button to start recording.',
          life: 4000,
        });
        return;
      }

      const formData = new FormData();
      formData.append('file', finalBlob, 'recording.webm');
      formData.append('transcript', isEditingTranscript && editedTranscript ? editedTranscript : transcript);
      formData.append(
        'questionId',
        String(sessionQuestions[currentQuestion]?.id)
      );
      formData.append('userId', userId);
      formData.append('sessionId', String(sessionData.id));
      formData.append('interviewType', INTERVIEW_TYPES.PHONE_INTERVIEW);
      formData.append('runId', String(runId));

      const response = await api.post('/ai/analyze', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if ([200, 201].includes(response.status)) {
        toast.current?.show({
          severity: 'success',
          summary: 'Success!',
          detail: 'Analyzing your response and generating feedback.',
        });
        gotoNextQuestion();
      } else {
        throw new Error('Unexpected response status');
      }
      toggleSubmitting(false);
    } catch (error) {
      toggleSubmitting(false);
      captureException(error);
      toast.current?.show({
        severity: 'error',
        summary: '📤 Submission Failed',
        detail: 'Could not submit your answer. Please check your connection and try again.',
        life: 4000,
      });
    }
  };

  /* --------------------------------
   * Navigation
   * ------------------------------- */
  const gotoNextQuestion = async () => {
    try {
      const userId = firebase.auth().currentUser?.uid;
      if (!userId) return;
      await firebase
        .firestore()
        .collection(Collections.Runs)
        .doc(sessionId)
        .collection('list')
        .doc(runId)
        .update({ currentQuestion: currentQuestion + 1 });

      if (currentQuestion + 1 === sessionQuestions?.length) {
        await endSession();
        return;
      }
      setTranscript('');
      setEditedTranscript('');
      setIsEditingTranscript(false);
      setHasRecording(false);
      if (recordingUrl) {
        URL.revokeObjectURL(recordingUrl);
        setRecordingUrl(null);
      }
      setCurrentQuestion(currentQuestion + 1);
    } catch (error) {
      captureException(error);
      toast.current?.show({
        severity: 'error',
        summary: 'Navigation Error',
        detail: 'Failed to load the next question. Please try again.',
      });
    }
  };

  const skipQuestion = async (questionId: number, confirmed: boolean = false) => {
    if (!confirmed) {
      confirmDialog({
        message: 'Are you sure you want to skip this question? This action cannot be undone.',
        header: 'Skip Question',
        icon: 'pi pi-exclamation-triangle',
        defaultFocus: 'reject',
        accept: () => skipQuestion(questionId, true),
      });
      return;
    }

    try {
      if (!sessionQuestions) return;
      const temp = [...sessionQuestions];
      const idx = temp.findIndex((q) => q.id === questionId);
      if (idx === -1) return;

      temp[idx].skipped = true;
      await firebase
        .firestore()
        .collection(Collections.Runs)
        .doc(sessionId)
        .collection('list')
        .doc(runId)
        .update({ questions: temp });

      gotoNextQuestion();
    } catch (error) {
      captureException(error);
      toast.current?.show({
        severity: 'error',
        summary: 'Skip Error',
        detail: 'Failed to skip the question. Please try again.',
      });
    }
  };

  const endSession = async () => {
    try {
      const userId = firebase.auth().currentUser?.uid;
      if (!userId) return;
      toggleShowRunCompleteDialog(true);

      await firebase
        .firestore()
        .collection(Collections.Runs)
        .doc(sessionId)
        .collection('list')
        .doc(runId)
        .update({
          completed: true,
          status: 'Completed',
          currentQuestion: 0,
        });

      await firebase
        .firestore()
        .collection(Collections.Sessions)
        .doc(userId)
        .collection('list')
        .doc(sessionId)
        .update({
          completed: true,
          status: 'Completed',
        });

      // Increment practice attempt usage after completing the full session
      await apiUsageService.incrementPracticeAttemptUsage();

      // Handle goal progress updates from session completion
      try {
        await sessionCompletionService.notifySessionCompletion({
          sessionId: sessionId || '',
          userId: userId,
          runId: runId || '',
          overallScore: 0, // This should be updated with actual score when available
          sessionType: 'phone_interview',
          completedAt: new Date(),
          skills: {} // Can be populated with actual skill scores
        });
      } catch (goalError) {
        console.error('Error updating goals from session completion:', goalError);
        // Don't block session completion if goal update fails
      }

      setTranscript('');
    } catch (error) {
      captureException(error);
      toast.current?.show({
        severity: 'error',
        summary: 'End Session Error',
        detail: 'Failed to end the session. Please try again.',
      });
    }
  };

  // Keyboard shortcuts
  useHotkeys('space', (e) => {
    e.preventDefault();
    if (isRecording) {
      stopRecording();
    } else if (!submitting && hasPermission !== false) {
      startRecording();
    }
  }, [isRecording, submitting, hasPermission, stopRecording, startRecording]);

  useHotkeys('esc', (e) => {
    e.preventDefault();
    if (isRecording) {
      stopRecording();
    }
    if (isReading) {
      readQuestionAloud();
    }
    if (showRunCompleteDialog) {
      toggleShowRunCompleteDialog(false);
    }
  }, [isRecording, isReading, showRunCompleteDialog, stopRecording, readQuestionAloud]);

  useHotkeys('enter', (e) => {
    if (hasRecording && !submitting && !isRecording) {
      e.preventDefault();
      submitAnswer();
    }
  }, [hasRecording, submitting, isRecording, submitAnswer]);

  /* --------------------------------
   * Auto-read question when loaded or changed
   * ------------------------------- */
  useEffect(() => {
    // Only auto-read if enabled and we have session questions and the question index changed
    if (autoReadEnabled && sessionQuestions && currentQuestion !== hasAutoReadRef.current && !isReading && !isLoadingVoice) {
      // Mark this question as read to prevent reading again on re-renders
      hasAutoReadRef.current = currentQuestion;

      // Small delay to ensure UI is fully rendered
      const timer = setTimeout(() => {
        readQuestionAloud();
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [autoReadEnabled, currentQuestion, sessionQuestions, readQuestionAloud, isReading, isLoadingVoice]);

  // Reset the auto-read flag when moving to a new question
  useEffect(() => {
    return () => {
      // When unmounting or changing questions, cancel any ongoing speech
      if (isReading) {
        // Stop browser speech synthesis if it's being used
        if (speechSynthesis) {
          speechSynthesis.cancel();
        }

        // Stop API-based audio if it's playing
        if (audioRef.current) {
          audioRef.current.pause();
          audioRef.current.currentTime = 0;
        }
      }
    };
  }, [currentQuestion, isReading]);

  // Recording playback functions
  const playRecording = () => {
    if (!recordingUrl || !playbackAudioRef.current) return;
    playbackAudioRef.current.src = recordingUrl;
    playbackAudioRef.current.play();
    setIsPlayingRecording(true);
  };

  const stopPlayback = () => {
    if (playbackAudioRef.current) {
      playbackAudioRef.current.pause();
      playbackAudioRef.current.currentTime = 0;
    }
    setIsPlayingRecording(false);
  };

  useEffect(() => {
    if (!playbackAudioRef.current) {
      playbackAudioRef.current = new Audio();
      playbackAudioRef.current.addEventListener('ended', () => {
        setIsPlayingRecording(false);
      });
    }
    return () => {
      if (playbackAudioRef.current) {
        playbackAudioRef.current.removeEventListener('ended', () => { });
      }
    };
  }, []);

  /* --------------------------------
   * Render
   * ------------------------------- */

  // Loading skeleton
  if (isLoading || !sessionData || !sessionQuestions) {
    return (
      <div className="min-h-screen flex flex-col bg-gray-50">
        <div className="sticky top-0 z-40 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 text-white p-3 sm:p-4">
          <div className="flex items-center justify-between gap-2 sm:gap-4">
            <div className="h-8 w-16 bg-white bg-opacity-20 rounded animate-pulse"></div>
            <div className="h-6 w-48 bg-white bg-opacity-20 rounded animate-pulse"></div>
            <div className="h-8 w-24 bg-white bg-opacity-20 rounded animate-pulse"></div>
          </div>
        </div>
        <main className="flex-1 p-4 sm:p-6 grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
          <div className="bg-white p-4 sm:p-6 rounded-lg shadow-lg space-y-4">
            <div className="h-24 bg-gray-200 rounded animate-pulse"></div>
            <div className="h-32 bg-gray-200 rounded animate-pulse"></div>
          </div>
          <div className="bg-white p-4 sm:p-6 rounded-lg shadow-lg space-y-4">
            <div className="h-24 bg-gray-200 rounded animate-pulse"></div>
            <div className="h-48 bg-gray-200 rounded animate-pulse"></div>
          </div>
        </main>
      </div>
    );
  }

  // Loading overlay while submitting
  if (submitting) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-gradient-to-bl from-blue-50 to-white">
        <DotLottieReact
          src="https://lottie.host/f7b5bebc-ce92-4136-9295-815dea9c1920/P0BpzInq7K.lottie"
          loop
          autoplay
          className="w-32 h-32"
        />
        <div className="mt-2 text-gray-700">
          <small>Uploading your response, please wait...</small>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Toast ref={toast} />
      <ConfirmDialog />

      {/* ---------- HEADER (Sticky, Mobile-optimized) ---------- */}
      <header className="sticky top-0 z-40 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 text-white p-3 sm:p-4 shadow-lg">
        <div className="flex items-center justify-between gap-2 sm:gap-4">
          {/* Back Button */}
          <motion.button
            onClick={() => navigate(-1)}
            className="flex items-center text-white hover:text-gray-200 transition p-2 sm:px-3 sm:py-2 min-w-[44px] min-h-[44px] sm:min-w-0 sm:min-h-0"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            disabled={submitting}
            aria-label="Go back"
          >
            <FaArrowLeft className="text-base sm:text-lg" />
            <span className="hidden sm:inline ml-2 text-sm font-medium">Back</span>
          </motion.button>

          {/* Title & Timer (Mobile: Stack, Desktop: Side by side) */}
          <div className="flex-1 flex flex-col sm:flex-row items-center justify-center sm:justify-between gap-2 sm:gap-4 min-w-0">
            <motion.h1
              className="text-base sm:text-lg md:text-xl lg:text-2xl font-bold text-center truncate max-w-full"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              title={sessionData?.title || 'Interview Session'}
            >
              {sessionData?.title || 'Interview Session'}
            </motion.h1>

            {/* Timer - Always visible, compact on mobile */}
            <div className="flex items-center text-xs sm:text-sm">
              <FaStopwatch className="mr-1 sm:mr-2" />
              <Countdown
                startSeconds={timeRemaining || 0}
                dontCountSignal={showRunCompleteDialog || submitting}
                sessionId={sessionData?.id || ''}
                runId={runId || ''}
                onComplete={endSession}
                interviewType={INTERVIEW_TYPES.PHONE_INTERVIEW}
              />
            </div>
          </div>

          {/* End Session Button */}
          <motion.button
            onClick={endSession}
            className="bg-red-500 px-3 sm:px-4 py-2 rounded-lg hover:bg-red-600 transition flex items-center text-xs sm:text-sm font-semibold min-w-[44px] min-h-[44px] sm:min-w-0 sm:min-h-0 justify-center"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            disabled={submitting}
            aria-label="End session"
          >
            <span className="hidden sm:inline">End Session</span>
            <span className="sm:hidden">End</span>
          </motion.button>
        </div>
      </header>

      {/* ---------- MAIN CONTENT (Responsive Grid) ---------- */}
      <main className="flex-1 p-4 sm:p-6 grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {/* ----- INTERVIEWER PANEL ----- */}
        <motion.section
          className="bg-white p-4 sm:p-6 rounded-lg shadow-lg flex flex-col"
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Interviewer Profile Card - Mobile optimized */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center mb-4 gap-3 sm:gap-4">
            <div className="relative flex-shrink-0">
              <img
                src={sessionData?.interviewer?.image || 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?q=80&w=300&h=300&auto=format&fit=crop'}
                alt={sessionData?.interviewer?.name || 'Interviewer'}
                className={`w-12 h-12 sm:w-16 sm:h-16 rounded-full object-cover border-2 ${interviewerSpeaking ? 'border-blue-500 animate-pulse' : 'border-gray-300'}`}
              />
              {interviewerSpeaking && (
                <div className="absolute -bottom-1 -right-1 bg-blue-500 rounded-full p-1">
                  <FaVolumeUp className="text-white text-xs" />
                </div>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-base sm:text-lg font-semibold truncate">
                {sessionData?.interviewer?.name || 'Your Interviewer'}
              </h3>
              <p className="text-xs sm:text-sm text-gray-600 truncate">
                {sessionData?.interviewer?.title || 'Interview Session'}
              </p>
            </div>

            {/* Question Counter - More prominent, better mobile layout */}
            <div className="w-full sm:w-auto sm:ml-auto">
              <div className="flex items-center justify-between sm:justify-end text-xs sm:text-sm text-gray-600 mb-1">
                <span className="font-semibold">
                  Question {currentQuestion + 1} of {sessionQuestions?.length || 0}
                </span>
              </div>
              <div className="relative h-2 w-full sm:w-32 bg-gray-200 rounded-full">
                <motion.div
                  className="absolute top-0 left-0 h-full bg-gradient-to-r from-indigo-500 to-purple-600 rounded-full"
                  initial={{ width: 0 }}
                  animate={{ width: `${((currentQuestion + 1) / (sessionQuestions?.length || 1)) * 100}%` }}
                  transition={{ duration: 0.8, ease: "easeOut" }}
                />
              </div>
            </div>
          </div>

          {/* Conversation/Question Bubble - Fixed overlap issue */}
          <div className="mb-4 flex-1">
            <div className={`p-4 sm:p-6 rounded-lg bg-blue-50 border-l-4 border-blue-500 relative transition-all ${interviewerSpeaking ? 'shadow-md border-blue-600' : ''}`}>
              {/* Voice control button - Better positioning, larger touch target */}
              <div className="absolute top-3 right-3 flex items-center gap-2">
                {apiError && (
                  <span className="text-xs text-red-500 hidden sm:inline">
                    {apiError === 'Failed to generate premium voice' ? 'Using fallback' : ''}
                  </span>
                )}
                <motion.button
                  onClick={readQuestionAloud}
                  className={`min-w-[44px] min-h-[44px] flex items-center justify-center rounded-full ${isLoadingVoice
                    ? 'bg-gray-400 text-white'
                    : isReading
                      ? 'bg-red-500 text-white'
                      : 'bg-gray-200 text-gray-700'
                    } hover:bg-purple-600 hover:text-white transition`}
                  whileHover={!isLoadingVoice ? { scale: 1.1 } : {}}
                  whileTap={!isLoadingVoice ? { scale: 0.95 } : {}}
                  aria-label={isReading ? "Stop reading question" : "Read question aloud"}
                  title={isReading ? "Stop reading question" : "Read question aloud"}
                  disabled={isLoadingVoice}
                >
                  {isLoadingVoice ? (
                    <FaSpinner className="animate-spin text-base sm:text-lg" />
                  ) : isReading ? (
                    <FaStop className="text-base sm:text-lg" />
                  ) : (
                    <FaVolumeUp className="text-base sm:text-lg" />
                  )}
                </motion.button>
              </div>
              <div className="pr-16 sm:pr-20">
                <p className={`text-gray-800 text-base sm:text-lg md:text-xl leading-relaxed ${interviewerSpeaking ? 'text-blue-700 font-medium' : ''}`}>
                  {sessionQuestions?.[currentQuestion]?.question || 'Loading question...'}
                </p>
              </div>

              {/* Visual indicator for speech */}
              {interviewerSpeaking && (
                <div className="mt-4 flex justify-center">
                  <div className="flex items-end space-x-1">
                    <div className="w-1 h-3 bg-blue-400 rounded-full animate-sound-wave-1"></div>
                    <div className="w-1 h-5 bg-blue-500 rounded-full animate-sound-wave-2"></div>
                    <div className="w-1 h-7 bg-blue-600 rounded-full animate-sound-wave-3"></div>
                    <div className="w-1 h-5 bg-blue-500 rounded-full animate-sound-wave-2"></div>
                    <div className="w-1 h-3 bg-blue-400 rounded-full animate-sound-wave-1"></div>
                  </div>
                </div>
              )}
            </div>

            {/* Tips section - Collapsible */}
            <div className="mt-4 bg-gray-50 rounded-lg border border-gray-200 overflow-hidden">
              <motion.button
                onClick={() => setShowTips(!showTips)}
                className="w-full p-3 sm:p-4 flex items-center justify-between font-semibold text-gray-700 text-sm uppercase hover:bg-gray-100 transition"
                whileTap={{ scale: 0.98 }}
                aria-label={showTips ? "Hide tips" : "Show tips"}
              >
                <div className="flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>Tips from your interviewer</span>
                </div>
                {showTips ? <FaChevronUp /> : <FaChevronDown />}
              </motion.button>
              <AnimatePresence>
                {showTips && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="overflow-hidden"
                  >
                    <ul className="p-3 sm:p-4 pt-0 list-disc list-inside text-xs sm:text-sm text-gray-600 space-y-1.5">
                      <li>Speak clearly and at a moderate pace</li>
                      <li>Use specific examples to illustrate your points</li>
                      <li>Structure your answer with a beginning, middle, and conclusion</li>
                      <li>Focus on your tone and verbal communication skills</li>
                    </ul>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Auto-read toggle */}
            <div className="mt-3 flex items-center justify-between text-xs sm:text-sm">
              <label className="flex items-center text-gray-600 cursor-pointer">
                <input
                  type="checkbox"
                  checked={autoReadEnabled}
                  onChange={(e) => setAutoReadEnabled(e.target.checked)}
                  className="mr-2 w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500"
                />
                <span>Auto-read questions</span>
              </label>
            </div>
          </div>
        </motion.section>

        {/* ----- AUDIO RECORDING PANEL ----- */}
        <motion.section
          className="flex flex-col space-y-4"
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Speaking Indicator Card - Compact when not recording */}
          <AnimatePresence>
            {(isRecording || isSpeaking) && (
              <motion.div
                className={`p-3 sm:p-4 rounded-lg shadow-lg flex flex-col items-center justify-center transition-all ${isSpeaking
                  ? 'bg-green-50 border border-green-300'
                  : 'bg-white border border-gray-200'
                  }`}
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.2 }}
              >
                <div className="flex items-center mb-2 sm:mb-3 w-full">
                  <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center ${isSpeaking ? 'bg-green-500' : 'bg-gray-200'} mr-3 flex-shrink-0`}>
                    <FaMicrophone className={`text-base sm:text-lg ${isSpeaking ? 'text-white' : 'text-gray-500'}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-bold text-gray-800 text-sm sm:text-base">Your Microphone</h3>
                    <p className="text-xs sm:text-sm text-gray-600">
                      {isSpeaking ? 'You are speaking...' : 'Listening...'}
                    </p>
                  </div>
                  {/* Audio Level Indicator */}
                  {isRecording && (
                    <div className="flex items-center gap-1 ml-2">
                      <div className="w-1 h-4 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all ${audioLevel > 30 ? 'bg-green-500' : audioLevel > 10 ? 'bg-yellow-500' : 'bg-red-500'}`}
                          style={{ width: `${audioLevel}%` }}
                        />
                      </div>
                      {audioLevel < 10 && (
                        <MdWarning className="text-yellow-500 text-xs" title="Audio level is low" />
                      )}
                    </div>
                  )}
                </div>

                {isSpeaking && (
                  <div className="w-full flex justify-center mt-2">
                    <div className="flex items-end space-x-1">
                      <div className="w-1 h-3 bg-green-300 rounded-full animate-sound-wave-1"></div>
                      <div className="w-1 h-8 bg-green-500 rounded-full animate-sound-wave-2"></div>
                      <div className="w-1 h-12 bg-green-600 rounded-full animate-sound-wave-3"></div>
                      <div className="w-1 h-8 bg-green-500 rounded-full animate-sound-wave-2"></div>
                      <div className="w-1 h-3 bg-green-300 rounded-full animate-sound-wave-1"></div>
                    </div>
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Permission Status */}
          {hasPermission === false && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 sm:p-4">
              <div className="flex items-start gap-3">
                <MdWarning className="text-yellow-600 text-lg flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-yellow-800 mb-1">Microphone Access Required</p>
                  <p className="text-xs text-yellow-700 mb-2">Please allow microphone access to record your answers.</p>
                  <motion.button
                    onClick={requestMicrophonePermission}
                    disabled={isRequestingPermission}
                    className="bg-yellow-600 text-white px-3 py-1.5 rounded text-xs font-semibold hover:bg-yellow-700 transition disabled:opacity-50"
                    whileTap={{ scale: 0.95 }}
                  >
                    {isRequestingPermission ? 'Requesting...' : 'Grant Permission'}
                  </motion.button>
                </div>
              </div>
            </div>
          )}

          {/* Recording Controls & Transcript */}
          <motion.div
            className="bg-white p-4 sm:p-6 rounded-lg shadow-lg flex-1 flex flex-col"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            {/* Recording Controls - Larger touch targets */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4">
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3 w-full sm:w-auto">
                {!isRecording && (
                  <motion.button
                    onClick={startRecording}
                    disabled={submitting || hasPermission === false || isRequestingPermission}
                    className="bg-indigo-500 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg hover:bg-indigo-600 transition flex items-center justify-center text-sm sm:text-base font-semibold min-w-[120px] min-h-[44px] disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto"
                    whileHover={!submitting && hasPermission !== false ? { scale: 1.02 } : {}}
                    whileTap={!submitting && hasPermission !== false ? { scale: 0.98 } : {}}
                    aria-label="Start recording"
                  >
                    <FaMicrophone className="mr-2 text-base sm:text-lg" />
                    <span className="hidden sm:inline">Start Recording</span>
                    <span className="sm:hidden">Record</span>
                  </motion.button>
                )}
                {isRecording && (
                  <motion.button
                    onClick={stopRecording}
                    className="bg-red-500 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg hover:bg-red-600 transition flex items-center justify-center text-sm sm:text-base font-semibold min-w-[120px] min-h-[44px] w-full sm:w-auto"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    disabled={submitting}
                    aria-label="Stop recording"
                  >
                    <FaStop className="mr-2 text-base sm:text-lg" />
                    <span className="hidden sm:inline">Stop Recording</span>
                    <span className="sm:hidden">Stop</span>
                  </motion.button>
                )}
              </div>

              {/* Recording Countdown - Larger, color-coded */}
              {isRecording && (
                <motion.div
                  className={`font-semibold flex items-center text-sm sm:text-base ${recordingTimeLeft <= 30 ? recordingTimeLeft <= 10 ? 'text-red-600' : 'text-orange-600' : 'text-gray-800'}`}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.5 }}
                >
                  <FaStopwatch className={`mr-2 ${recordingTimeLeft <= 10 ? 'animate-pulse' : ''}`} />
                  <span>
                    {recordingTimeLeft <= 10 && '⚠️ '}
                    {formatTime(recordingTimeLeft)}
                  </span>
                </motion.div>
              )}
            </div>

            {/* Transcript - Editable, larger area */}
            <motion.div
              className="mt-4 flex-grow"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
            >
              <div className="flex justify-between items-center mb-2">
                <p className="font-bold text-gray-800 flex items-center text-sm sm:text-base">
                  <FaMicrophone className={isListening ? "mr-2 text-green-500" : "mr-2 text-gray-400"} />
                  Your Response:
                </p>
                <div className="flex items-center gap-2">
                  {isRecording && isListening && (
                    <span className="text-xs text-green-500 animate-pulse">Live transcription</span>
                  )}
                  {!isRecording && transcript && (
                    <motion.button
                      onClick={() => {
                        setIsEditingTranscript(!isEditingTranscript);
                        if (!isEditingTranscript) {
                          setEditedTranscript(transcript);
                        }
                      }}
                      className="text-xs text-indigo-600 hover:text-indigo-800 flex items-center gap-1"
                      whileTap={{ scale: 0.95 }}
                    >
                      {isEditingTranscript ? (
                        <>
                          <FaCheck className="text-green-600" />
                          <span className="hidden sm:inline">Done</span>
                        </>
                      ) : (
                        <>
                          <FaEdit />
                          <span className="hidden sm:inline">Edit</span>
                        </>
                      )}
                    </motion.button>
                  )}
                </div>
              </div>
              {isEditingTranscript ? (
                <textarea
                  value={editedTranscript}
                  onChange={(e) => setEditedTranscript(e.target.value)}
                  className="w-full min-h-[150px] sm:min-h-[200px] max-h-[300px] text-gray-700 whitespace-pre-wrap p-4 rounded-lg bg-white border-2 border-indigo-300 focus:border-indigo-500 focus:outline-none resize-y text-sm sm:text-base"
                  placeholder="Edit your transcript here..."
                />
              ) : (
                <div className="min-h-[150px] sm:min-h-[200px] max-h-[300px] text-gray-700 whitespace-pre-wrap p-4 rounded-lg bg-gray-50 border-r-4 border-gray-300 overflow-y-auto text-sm sm:text-base">
                  {transcript || 'Your speech will appear here...'}
                </div>
              )}
            </motion.div>

            {/* Recording Preview & Submit */}
            {hasRecording && !isRecording && (
              <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-semibold text-green-800">Recording Ready</p>
                  <div className="flex items-center gap-2">
                    {recordingUrl && (
                      <motion.button
                        onClick={isPlayingRecording ? stopPlayback : playRecording}
                        className="flex items-center gap-2 px-3 py-1.5 bg-green-600 text-white rounded text-xs font-semibold hover:bg-green-700 transition"
                        whileTap={{ scale: 0.95 }}
                      >
                        {isPlayingRecording ? (
                          <>
                            <FaStop className="text-xs" />
                            <span>Stop</span>
                          </>
                        ) : (
                          <>
                            <FaPlay className="text-xs" />
                            <span>Play</span>
                          </>
                        )}
                      </motion.button>
                    )}
                  </div>
                </div>
                <p className="text-xs text-green-700">You can review your recording before submitting.</p>
              </div>
            )}

            {/* Submit Answer Button - Fixed visibility */}
            <AnimatePresence>
              {hasRecording && !isRecording && (
                <motion.div
                  className="flex justify-end mt-4"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 10 }}
                  transition={{ duration: 0.3 }}
                >
                  <motion.button
                    onClick={submitAnswer}
                    className="bg-green-500 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg hover:bg-green-600 transition font-semibold flex items-center text-sm sm:text-base min-h-[44px]"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    disabled={submitting}
                    aria-label="Submit answer"
                  >
                    <MdSend className="mr-2 text-base sm:text-lg" />
                    Submit Answer
                  </motion.button>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </motion.section>
      </main>

      {/* ----- FOOTER (Enhanced) ----- */}
      <footer className="p-4 sm:p-6 bg-white border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-4">
        <motion.button
          onClick={() => {
            const qid = sessionQuestions?.[currentQuestion]?.id;
            if (qid) skipQuestion(qid);
          }}
          className="flex items-center text-gray-700 px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg hover:bg-gray-100 transition text-sm font-semibold min-w-[120px] min-h-[44px] justify-center"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          disabled={submitting}
          aria-label="Skip question"
        >
          <MdSkipNext className="mr-2" />
          Skip Question
        </motion.button>
        <div className="text-xs sm:text-sm text-gray-500">
          <span className="hidden sm:inline">Keyboard shortcuts: </span>
          <span className="font-mono bg-gray-100 px-2 py-1 rounded">Space</span> to record,{' '}
          <span className="font-mono bg-gray-100 px-2 py-1 rounded">Enter</span> to submit
        </div>
      </footer>

      {/* ---------- COMPLETION OVERLAY ---------- */}
      <AnimatePresence>
        {showRunCompleteDialog && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70 p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => toggleShowRunCompleteDialog(false)}
          >
            {/* Confetti */}
            <div className="absolute inset-0 pointer-events-none">
              <Confetti width={width} height={height} />
            </div>

            <motion.div
              className="relative bg-white rounded-lg shadow-lg p-6 sm:p-8 text-center flex flex-col items-center max-w-md w-full"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ type: 'spring', stiffness: 300, damping: 25 }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => toggleShowRunCompleteDialog(false)}
                className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 text-xl"
                aria-label="Close"
              >
                ×
              </button>
              <DotLottieReact
                src="https://lottie.host/392d3f6c-7239-46fc-9b41-ca5a9814504d/hFXbyazAMW.lottie"
                loop
                autoplay
                className="w-32 h-32 sm:w-40 sm:h-40 mx-auto mb-4"
              />
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-4">
                Session Complete!
              </h2>
              <p className="text-gray-600 mb-6 text-xs sm:text-sm">
                Congratulations on completing your session.
                We'll analyze your responses and compile your feedback shortly.
              </p>
              <motion.button
                onClick={() => navigate(-1)}
                className="bg-indigo-500 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg hover:bg-indigo-600 transition font-semibold text-sm sm:text-base min-h-[44px]"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                disabled={submitting}
              >
                Go Back
              </motion.button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AudioActiveSession;
